/* eslint-disable no-param-reassign */
import { createAsyncThunk } from '@reduxjs/toolkit';
import { NEW_PATH_DELIMITER } from '@picsio/db/src/constants';
import { MAX_GROUP_DEPTH } from '@picsio/db/src/collections/savedSearches/constants';
import {
  makeTree,
  setField,
  DEFAULT_SAVED_SEARCH_TREE_SORT,
  mergePreservedEmptyGroupPlaceholder,
} from '../helpers/savedSearches';
import { fullPathUnderParent } from '../helpers/savedSearches/pathConflict';

export const move = createAsyncThunk(
  'savedSearches/move',
  async (
    { _id, path },
    {
      extra: {
        sdk, Logger, Toast, localization: l10n, utils,
      },
      getState,
    },
  ) => {
    try {
      const { data: moveResult } = await sdk.savedSearches.move(_id, path || '');

      return {
        _id,
        path,
        userId: getState().user._id,
        preservedPlaceholder: moveResult?.preservedPlaceholder || null,
      };
    } catch (error) {
      Logger.error(new Error('Can not move saved search'), { error }, [
        'MoveSavedSearchesFailed',
        { errorMessage: (error && error.message) || 'NoMessage' },
      ]);

      const status = utils.getStatusFromResponceError(error);
      const data = error?.response?.data;
      const serverMsg = typeof data === 'string' ? data : (data?.message || data?.msg || '');
      const movedEntity = getState().savedSearches.all.find((ss) => ss._id === _id);
      const isGroupMove = Boolean(movedEntity?.isGroupPlaceholder);
      let toastMsg = 'Error moving saved search';
      if (status === 409) {
        toastMsg = isGroupMove
          ? l10n.SAVEDSEARCHESTREE.textDuplicateGroupInLocation
          : l10n.SAVEDSEARCHESTREE.textDuplicateSavedSearchInLocation;
      } else if (status === 400) {
        toastMsg = /depth|limit/i.test(serverMsg)
          ? l10n.SAVEDSEARCHESTREE.textMoveGroupDepthExceeded(MAX_GROUP_DEPTH)
          : l10n.SAVEDSEARCHESTREE.textMoveGroupInvalidTarget;
      }
      Toast(toastMsg, { type: 'error' });

      throw error;
    }
  },
);

export const reducer = (builder) => {
  builder.addCase(
    move.pending,
    (state, { meta: { arg: { _id } } }) => {
      state.tree = setField(state.tree, _id, 'isBusy', true);
    },
  ).addCase(
    move.fulfilled,
    (state, { meta: { arg: { _id } }, payload }) => {
      if (!payload) {
        state.tree = setField(state.tree, _id, 'isBusy', false);
        return;
      }

      const { path: destParentPath, userId, preservedPlaceholder } = payload;
      const parentPath = destParentPath || '';

      const moved = state.all.find((ss) => ss._id === _id);

      if (moved?.isGroupPlaceholder && moved.path) {
        const oldPrefix = moved.path;
        const newRootPath = fullPathUnderParent(parentPath, moved.name);
        const movedIsShared = Boolean(moved.teamId);
        state.all = state.all.map((ss) => {
          if (!ss.path) return ss;
          const sameScope = movedIsShared ? Boolean(ss.teamId) : !ss.teamId;
          if (!sameScope) return ss;
          if (ss.path === oldPrefix) {
            return { ...ss, path: newRootPath };
          }
          if (ss.path.startsWith(oldPrefix + NEW_PATH_DELIMITER)) {
            return { ...ss, path: newRootPath + ss.path.slice(oldPrefix.length) };
          }
          return ss;
        });
      } else {
        state.all = state.all.map((ss) => {
          if (ss._id !== _id) return ss;
          if (!parentPath) {
            return Object.fromEntries(
              Object.entries(ss).filter(([key]) => key !== 'path'),
            );
          }
          return {
            ...ss,
            path: fullPathUnderParent(parentPath, ss.name),
          };
        });
      }

      state.all = mergePreservedEmptyGroupPlaceholder(state.all, preservedPlaceholder);

      state.tree = makeTree(
        state.all,
        userId,
        state.searchQuery,
        state.treeSortType || DEFAULT_SAVED_SEARCH_TREE_SORT,
      );
    },
  ).addCase(
    move.rejected,
    (state, { error, meta: { arg: { _id } } }) => {
      state.error = error;
      state.tree = setField(state.tree, _id, 'isBusy', false);
    },
  );
};
